// Provider de langue (Phase 95, plan 95-04).
//
// Patron repris trait pour trait d'auth/AuthProvider.tsx — le seul contexte
// React du projet : dépendance sur `userId`, garde `cancelled`, tout le
// setState dans le callback asynchrone (react-hooks/set-state-in-effect).
//
// Trois faits structurent ce fichier :
//   · l'état initial est calculé DANS l'initialiseur de useState, donc avant le
//     premier rendu — c'est la synchronicité de `readStored()` puis de la
//     détection navigateur qui supprime le clignotement de langue (I18N-01) ;
//   · le catalogue anglais n'entre que par un import() dynamique (D-10) —
//     Rollup en fait un chunk tout seul, aucun `manualChunks` à ajouter ;
//   · `setLang` est la bascule EXPLICITE : elle n'est appelée que par un clic
//     sur le sélecteur, jamais par la réconciliation (D-06).
// Refs: phase 95.
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import RouteSkeleton from '../components/RouteSkeleton'
import { LanguageContext, type LanguageContextValue } from './LanguageContext'
import { detectFromNavigator } from './detect'
import { langFromPath } from './urlLang'
import { LANG_STORAGE_KEY, readStored, writeStored } from './storage'
import { translate } from './translate'
import type { Catalogue, Lang, MsgKey } from './types'

export function LanguageProvider({
  children,
  initialLang,
}: {
  children: ReactNode
  /** Court-circuite la détection — n'existe que pour le harnais de test. */
  initialLang?: Lang
}) {
  // D-03 — l'URL fait foi, et elle SEULE. Déstructuration sans setter : la
  // langue rendue est constante pour la durée du document. Une bascule est
  // désormais une navigation (D-05), pas un changement d'état.
  // `initialLang` reste prioritaire : c'est l'échappatoire du harnais de test,
  // dont dépendent les 46 suites montées par renderWithProviders.
  const [lang] = useState<Lang>(
    () => initialLang ?? langFromPath(window.location.pathname) ?? 'fr',
  )
  // D-07 bis — la préférence enregistrée, à défaut la détection navigateur.
  // Elle ne commande AUCUN rendu : elle décide seulement si le bandeau
  // « Switch to English » s'affiche, et la réconciliation la met à jour quand
  // le compte porte une langue (c'est ce qui garde I18N-02 / I18N-03
  // observables après D-03).
  const [suggested, setSuggested] = useState<Lang>(
    () => readStored()?.lang ?? detectFromNavigator(navigator.languages ?? [navigator.language]),
  )
  const [enCatalogue, setEnCatalogue] = useState<Catalogue | null>(null)
  const [enFailed, setEnFailed] = useState(false)
  const { user } = useAuth()
  const userId = user?.id

  // Le français n'a RIEN à charger : fr.ts est déjà là en import statique et
  // sert de repli à translate(). `catalogue === null` veut donc dire « rends du
  // français », pas « en attente ».
  const catalogue = lang === 'en' ? enCatalogue : null
  const loading = lang === 'en' && enCatalogue === null && !enFailed

  useEffect(() => {
    document.documentElement.lang = lang
    if (lang !== 'en' || enCatalogue || enFailed) return
    let cancelled = false
    void import('./locales/en')
      .then((m) => {
        if (!cancelled) setEnCatalogue(m.en)
      })
      .catch(() => {
        // Chunk injoignable : repli français, jamais d'écran blanc (T-95-10).
        if (!cancelled) setEnFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [lang, enCatalogue, enFailed])

  // D-06 — appelée UNIQUEMENT par un clic sur le sélecteur. L'écriture passe
  // par la RPC SECURITY DEFINER, dont la ligne cible vient de `auth.uid()`
  // côté serveur : le client n'envoie jamais d'identifiant de ligne. Une
  // écriture directe sur la table depuis le client retournerait 42501 —
  // `authenticated` n'a pas le GRANT (bug de la Phase 90 mot pour mot).
  // D-05 — elle ne change plus l'état : son appelant NAVIGUE vers la jumelle
  // juste après. L'écriture de stockage est synchrone (rien ne peut la perdre),
  // la RPC est ATTENDUE — une requête en vol meurt avec le document, et la
  // préférence n'atteindrait jamais le compte. En anonyme il n'y a rien à
  // attendre.
  const setLang = useCallback(
    async (next: Lang) => {
      writeStored({ lang: next, source: 'explicit' })
      if (userId) await supabase.rpc('set_own_language', { p_language: next })
    },
    [userId],
  )

  // Réconciliation compte ↔ navigateur. Même forme d'effet que la résolution de
  // rôle d'AuthProvider.
  const prevUserIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!userId) {
      // Déconnexion = passage d'un compte à rien. La préférence TIRÉE du compte
      // qui part est effacée : la laisser la ferait hériter par la personne
      // suivante sur un navigateur partagé — une préférence qui fuit d'un
      // utilisateur à un autre. Un `source: 'explicit'` survit, lui : il
      // appartient à la personne devant l'écran, c'est exactement ce que D-09
      // protège.
      if (prevUserIdRef.current && readStored()?.source === 'account') {
        try {
          window.localStorage.removeItem(LANG_STORAGE_KEY)
        } catch {
          // Stockage inaccessible (mode privé strict) : rien à effacer.
        }
      }
      prevUserIdRef.current = undefined
      return
    }
    prevUserIdRef.current = userId

    let cancelled = false
    ;(async () => {
      // La réconciliation est un confort, jamais un chemin critique : réseau
      // coupé, PostgREST en vrac ou ligne absente, on garde la langue courante.
      // Sans cette garde, l'échec remonterait en rejet non traité.
      let data: { language: Lang | null } | null = null
      try {
        const res = await supabase
          .from('profiles')
          .select('language')
          .eq('id', userId)
          .maybeSingle<{ language: Lang | null }>()
        data = res.data
      } catch {
        return
      }
      if (cancelled) return

      const stored = readStored()
      if (stored?.source === 'explicit') {
        // D-05 — le clic gagne et s'aligne sur le compte pour les autres
        // appareils. On ne réécrit pas une valeur identique.
        if (data?.language !== stored.lang) {
          void supabase.rpc('set_own_language', { p_language: stored.lang })
        }
        return
      }
      if (data?.language) {
        // D-06 + D-03 — le compte alimente la SUGGESTION, jamais la page. Pas
        // de garde `if (langFromPath(...)) return` ici : elle protégerait
        // `/en/packs` d'un compte français, mais laisserait le cas symétrique
        // ouvert — un compte anglais anglicisant `/packs`, que D-03 interdit
        // tout autant. C'est une suppression, pas une condition.
        setSuggested(data.language)
        writeStored({ lang: data.language, source: 'account' })
        return
      }
      // Compte NULL + aucun clic local : on ne touche à RIEN, ni à l'état ni au
      // compte. Une détection navigateur n'est PAS un choix (D-06 littéral).
      // C'est la branche qu'il est tentant de « compléter » — elle doit rester
      // vide.
    })()
    return () => {
      cancelled = true
    }
  }, [userId])

  const t = useMemo(
    () =>
      (key: MsgKey, vars?: Record<string, string | number>): string =>
        translate(key, lang, catalogue, vars),
    [lang, catalogue],
  )

  const value: LanguageContextValue = useMemo(
    () => ({ lang, suggested, loading, setLang, t }),
    [lang, suggested, loading, setLang, t],
  )

  // `loading` n'est vrai qu'en anglais : geler l'écran évite au visiteur une
  // frame de français avant l'arrivée du catalogue. Plus de garde sur la langue
  // de démarrage — `lang` ne bouge plus de tout le document (D-03).
  return (
    <LanguageContext.Provider value={value}>
      {loading ? <RouteSkeleton /> : children}
    </LanguageContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- même convention que useAuth : provider + hook dans le même fichier (CONVENTIONS.md)
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider')
  return ctx
}
